import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, Spinner } from 'react-bootstrap';
import ccmService from '../../services/ccmService';
import './EnrolledPatients.css';

const EnrolledPatients = () => {
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    practiceId: '',
    status: 'active',
    programType: '',
    page: 1,
    perPage: 20
  });

  useEffect(() => {
    fetchEnrolledPatients();
  }, [filters]);

  const fetchEnrolledPatients = async () => {
    try {
      setLoading(true);
      const response = await ccmService.getEnrolledPatients(filters);
      if (response.success) {
        setPatients(response.data || []);
        setPagination(response.pagination || {});
      }
    } catch (error) {
      console.error('Error fetching enrolled patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1
    }));
  };

  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}min`;
    }
    return `${mins}min`;
  };

  return (
    <Container fluid className="ccm-enp-container">
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <h4>Enrolled Patients</h4>
            </Card.Header>
            <Card.Body>
              {/* Filters */}
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Practice</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Practice ID"
                      value={filters.practiceId}
                      onChange={(e) => handleFilterChange('practiceId', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                    >
                      <option value="active">Active</option>
                      <option value="disenrolled">Disenrolled</option>
                      <option value="pending">Pending</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Program Type</Form.Label>
                    <Form.Select
                      value={filters.programType}
                      onChange={(e) => handleFilterChange('programType', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="CCM">CCM</option>
                      <option value="PCM">PCM</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3} className="d-flex align-items-end">
                  <Button variant="primary" onClick={fetchEnrolledPatients}>
                    Filter
                  </Button>
                </Col>
              </Row>

              {/* Table */}
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <>
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Patient Name</th>
                        <th>Program Type</th>
                        <th>Enrolled Date</th>
                        <th>Status</th>
                        <th>Billing Provider</th>
                        <th>Care Manager</th>
                        <th>Total Service Time</th>
                        <th>Last Service Date</th>
                        <th>Care Plan Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {patients.length === 0 ? (
                        <tr>
                          <td colSpan="10" className="text-center">
                            No enrolled patients found
                          </td>
                        </tr>
                      ) : (
                        patients.map((patient) => (
                          <tr key={patient.enrollment_id}>
                            <td>{patient.patient_name}</td>
                            <td><Badge bg="info">{patient.program_type}</Badge></td>
                            <td>{patient.enrollment_date ? new Date(patient.enrollment_date).toLocaleDateString() : 'N/A'}</td>
                            <td><Badge bg={patient.enrollment_status === 'active' ? 'success' : 'secondary'}>
                              {patient.enrollment_status}
                            </Badge></td>
                            <td>{patient.billing_provider || 'N/A'}</td>
                            <td>{patient.care_manager || 'N/A'}</td>
                            <td>{formatTime(patient.total_service_time)}</td>
                            <td>{patient.last_service_date ? new Date(patient.last_service_date).toLocaleDateString() : 'N/A'}</td>
                            <td><Badge bg={patient.care_plan_status === 'active' ? 'success' : 'warning'}>
                              {patient.care_plan_status || 'No Plan'}
                            </Badge></td>
                            <td>
                              <Button size="sm" variant="outline-primary" className="me-1">
                                View
                              </Button>
                              <Button size="sm" variant="outline-danger">
                                Disenroll
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>

                  {/* Pagination */}
                  {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div>
                        Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to{' '}
                        {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of{' '}
                        {pagination.total} entries
                      </div>
                      <div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={pagination.current_page === 1}
                          onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                        >
                          Previous
                        </Button>
                        <span className="mx-2">
                          Page {pagination.current_page} of {pagination.total_pages}
                        </span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          disabled={pagination.current_page === pagination.total_pages}
                          onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default EnrolledPatients;

